/**
 * A duration - days, hours and minutes - picked off three lists.
 *
 * WHY NOT THE CLOCK FACE THIS TIME. It was built as one first, and Damon was
 * right that it does not fit: a duration has no position on a dial. Four hours
 * and sixteen hours are the same place on the face, thirteen hours has to be
 * smuggled onto an inner ring, and "two days" cannot be said at all. A clock
 * answers "when"; this answers "how long", and those are different questions
 * that happen to share the word "hours".
 *
 * So: three columns you scroll and click, which is unambiguous, fast for
 * somebody who already knows the number, and honest about days.
 *
 * It lives behind a popover for the same reason the clock does - the RPO is
 * read off a customer's contract and entered once, and does not deserve
 * permanent room on the page.
 */

import { useEffect, useRef } from 'react';
import { Popover } from './Popover';
import { describeDuration, joinHours, shortDuration, splitHours } from './timeMath';

const DAYS = Array.from({ length: 15 }, (_, i) => i);
const HOURS = Array.from({ length: 24 }, (_, i) => i);
// Every minute would be a 60-row list to find "30" in. Five-minute steps cover
// every RPO anybody has ever written into a contract.
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);

export function DurationField({ value, onChange, disabled, label }: {
  /** Hours, fractional. Null means not set. */
  value: number | null;
  onChange: (hours: number | null) => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <Popover label={shortDuration(value)} title={label} disabled={disabled} wide>
      {() => <Columns value={value} onChange={onChange} />}
    </Popover>
  );
}

function Columns({ value, onChange }: {
  value: number | null;
  onChange: (hours: number | null) => void;
}) {
  const { d, h, m } = splitHours(value);
  // Round to the step the list actually offers, so an RPO of 4h07 set from
  // somewhere else still highlights a row rather than looking unset.
  const mSel = MINUTES.reduce((best, x) => (Math.abs(x - m) < Math.abs(best - m) ? x : best), 0);

  return (
    <div className="dur">
      <div className="dur-cols">
        <Col label="Days" values={DAYS} sel={d} onPick={(v) => onChange(joinHours(v, h, m))} />
        <Col label="Hours" values={HOURS} sel={h} onPick={(v) => onChange(joinHours(d, v, m))} />
        <Col label="Minutes" values={MINUTES} sel={mSel} pad onPick={(v) => onChange(joinHours(d, h, v))} />
      </div>
      <div className="dur-foot">
        <span className="dur-total">{describeDuration(value)}</span>
        {value != null && (
          <button type="button" className="btn-link" onClick={() => onChange(null)}>Clear</button>
        )}
      </div>
    </div>
  );
}

function Col({ label, values, sel, onPick, pad }: {
  label: string; values: number[]; sel: number; onPick: (v: number) => void; pad?: boolean;
}) {
  const box = useRef<HTMLDivElement | null>(null);

  // Scroll the current value into view when the popover opens. Without this a
  // 22-hour RPO opens showing 0 through 8 and looks like it was never set.
  useEffect(() => {
    const el = box.current?.querySelector('.dur-opt.on');
    if (el) (el as HTMLElement).scrollIntoView({ block: 'center' });
  }, []);

  return (
    <div className="dur-col">
      <div className="field-label">{label}</div>
      <div className="dur-list" ref={box}>
        {values.map((v) => (
          <button
            key={v}
            type="button"
            className={'dur-opt' + (v === sel ? ' on' : '')}
            onClick={() => onPick(v)}
          >
            {pad ? String(v).padStart(2, '0') : v}
          </button>
        ))}
      </div>
    </div>
  );
}
